import _ from 'lodash';
import { Tag, Icon, Typography, TextArea, Button } from '@scale/scaleui';
import { useCallback, useState, useMemo } from 'react';
import { CommentCard } from './CommentCard';
import { useQuery } from 'react-query';
import { fetchCommentsForExpert, addCommentForExpert, removeCommentById } from './api';
import { useCommentLayout } from './useCommentLayout';

interface CommentSectionProps {
  targetId: string;
}

export const CommentSection: React.FC<CommentSectionProps> = ({
  targetId,
}: CommentSectionProps) => {
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [newComment, setNewComment] = useState<string>('');
  const commentsQuery = useQuery([`comments`, targetId], () => fetchCommentsForExpert(targetId));

  const submitComment = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await addCommentForExpert(targetId, newComment);
      await commentsQuery.refetch();
      setNewComment('');
    } finally {
      setIsSubmitting(false);
    }
  }, [setIsSubmitting, targetId, newComment, setNewComment, commentsQuery]);

  const comments = useMemo(() => {
    const comments = commentsQuery.data?.comments || [];
    return _.reverse(_.sortBy(comments, 'createdAt'));
  }, [commentsQuery.data]);

  const handleRemoveComment = useCallback(
    async (commentId: string) => {
      await removeCommentById(targetId, commentId);
      commentsQuery.refetch();
    },
    [targetId, commentsQuery],
  );

  const { open, setOpen, getDrawerWidth } = useCommentLayout();

  return (
    <div
      className="fixed right-0 h-screen border-l border-neutral-200 bg-neutral-0"
      style={{ width: getDrawerWidth(), transition: 'width 150ms ease-in-out' }}
    >
      {open ? (
        <div className="flex flex-col gap-2 p-4">
          <div className="flex justify-end">
            <Icon icon="arrow-to-right" onClick={() => setOpen(false)} />
          </div>
          <Typography.Subtitle4>Comments ({comments.length})</Typography.Subtitle4>
          <TextArea
            id={`new-comment-input-${targetId}`}
            placeholder="Add a public comment..."
            disabled={isSubmitting}
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            rows={2}
          />
          <div className="flex w-full justify-end mb-4">
            <Button
              disabled={newComment.trim().length === 0 || isSubmitting}
              onClick={submitComment}
            >
              Submit Comment
            </Button>
          </div>
          {!commentsQuery.isLoading &&
            comments.map(comment => (
              <CommentCard
                key={comment._id}
                comment={comment.comment}
                createdBy={comment.author.firstName + ' ' + comment.author.lastName}
                createdAt={new Date(comment.createdAt)}
                onRemove={() => handleRemoveComment(comment._id)}
              />
            ))}
        </div>
      ) : (
        <div className="fixed right-0 p-4">
          <Tag
            className="shadow-md"
            size="lg"
            icon={['far', 'comment']}
            onClick={() => setOpen(true)}
          >
            {comments.length}
          </Tag>
        </div>
      )}
    </div>
  );
};
